'use client';

import React from 'react';
import Link from 'next/link';
import {
  Drawer as MuiDrawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  useTheme,
  useMediaQuery,
  IconButton
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Event as EventIcon,
  Email as EmailIcon,
  Settings as SettingsIcon,
  ListAlt as ListAltIcon
} from '@mui/icons-material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MenuIcon from '@mui/icons-material/Menu';
import { styled } from '@mui/material/styles';

const StyledDrawer = styled(MuiDrawer)<{ open: boolean }>(({ theme, open }) => ({
  width: 240,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  '& .MuiDrawer-paper': {
    width: 240,
    boxSizing: 'border-box',
    borderRight: 'none',
    overflowX: 'hidden',
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  [theme.breakpoints.up('sm')]: {
    ...(!open && {
      width: `calc(${theme.spacing(7)} + 1px)`,
      '& .MuiDrawer-paper': {
        width: `calc(${theme.spacing(7)} + 1px)`,
      },
    }),
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiDrawer-paper': {
      width: '85%',
      maxWidth: 320,
      boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
      zIndex: theme.zIndex.appBar + 2,
    },
  },
}));


const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

interface DrawerProps {
  open: boolean;
  onClose: () => void;
}

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { text: 'Leads', icon: <PeopleIcon />, path: '/leads' },
  { text: 'Mensajes', icon: <EmailIcon />, path: '/inbox' },
  { text: 'Calendario', icon: <EventIcon />, path: '/calendar' },
  { text: 'Actividad', icon: <ListAltIcon />, path: '/activities' },
];

export default function Drawer({ open, onClose }: DrawerProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleDrawerToggle = () => {
    onClose();
  };

  return (
    <StyledDrawer
      anchor="left"
      variant={isMobile ? 'temporary' : 'permanent'}
      open={open}
      onClose={handleDrawerToggle}
      ModalProps={{
        keepMounted: true,
        disableScrollLock: true,
        BackdropProps: {
          style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
        },
      }}
      PaperProps={{
        // Asegura que el papel del Drawer siga por encima en móvil
        sx: isMobile ? { zIndex: theme.zIndex.appBar + 2 } : undefined,
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        },
      }}
    >
      <DrawerHeader>
        <IconButton onClick={onClose}>
          {theme.direction === 'rtl' ? <MenuIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </DrawerHeader>

      <Box
        sx={{
          overflow: 'auto',
          height: '100%',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.divider,
            borderRadius: '4px',
          },
        }}
      >
        <List>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                component={Link}
                href={item.path}
                onClick={onClose}
                sx={{
                  minHeight: 48,
                  justifyContent: !isMobile && !open ? 'center' : 'initial',
                  px: 2.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: !isMobile && open ? 3 : 'auto',
                    justifyContent: 'center',
                    color: 'text.secondary',
                  }}
                >
                  {item.icon}
                </ListItemIcon>

                {/* En móvil, siempre mostramos el texto; en desktop depende de open (mini-variant) */}
                <ListItemText
                  primary={item.text}
                  sx={
                    isMobile
                      ? undefined
                      : {
                          opacity: open ? 1 : 0,
                          transition: theme.transitions.create('opacity', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.leavingScreen,
                          }),
                        }
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Divider />

        <List>
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              href="/settings"
              onClick={onClose}
              sx={{
                minHeight: 48,
                justifyContent: !isMobile && !open ? 'center' : 'initial',
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: !isMobile && open ? 3 : 'auto',
                  justifyContent: 'center',
                  color: 'text.secondary',
                }}
              >
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText
                primary="Ajustes"
                sx={{
                  opacity: isMobile || open ? 1 : 0,
                  transition: theme.transitions.create('opacity', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen,
                  }),
                }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </StyledDrawer>
  );
}
